// Scoped Lucide icon rendering.
//
// window.lucide.createIcons() (the CDN UMD) always rescans the WHOLE
// document for [data-lucide] elements. Our Stimulus controllers call it
// after every render, so a single interaction can trigger many full-document
// scans. renderIcons(root) does the same replacement but only within `root`
// (the just-rendered subtree), which is what almost every caller actually
// wants.
//
// It reuses lucide's own createElement() + icons map, so the emitted SVGs are
// byte-for-byte identical to createIcons(). If anything is missing (script not
// loaded yet, unexpected build), it falls back to the global whole-document
// pass so icons always render.

import { perfTime } from "perf"

function toPascalCase(kebab) {
  // Include digits so names like "trash-2" → "Trash2" (not "Trash-2").
  // With [a-z] only, the dash before a digit survived, so the lookup
  // L.icons["Trash-2"] missed and the <i> was left un-hydrated (an empty,
  // invisible element) — this is why delete/trash icons vanished.
  return String(kebab || "").replace(/(^|-)([a-z0-9])/g, (_, __, c) => c.toUpperCase())
}

export function renderIcons(root) {
  return perfTime("icons.render", () => _renderIcons(root))
}

function _renderIcons(root) {
  const L = typeof window !== "undefined" ? window.lucide : null
  if (!L) return
  const scope = (root && typeof root.querySelectorAll === "function") ? root : document
  const els = scope.querySelectorAll("[data-lucide]")
  if (!els.length) return

  // Fall back to lucide's own whole-document pass if the pieces we rely on
  // aren't present, so icons still appear.
  if (!L.icons || typeof L.createElement !== "function") {
    if (typeof L.createIcons === "function") L.createIcons()
    return
  }

  els.forEach((el) => {
    const name = el.getAttribute("data-lucide")
    const node = L.icons[toPascalCase(name)]
    if (!node) return
    let svg
    try { svg = L.createElement(node) } catch (_) { return }
    if (!svg) return
    // Mirror createIcons(): carry the <i>'s own attributes (sizing classes,
    // stroke/size overrides, aria, …) onto the SVG.
    const ownClass = el.getAttribute("class")
    for (const a of Array.from(el.attributes)) {
      if (a.name === "data-lucide" || a.name === "class") continue
      svg.setAttribute(a.name, a.value)
    }
    svg.classList.add("lucide", `lucide-${name}`)
    if (ownClass) ownClass.split(/\s+/).filter(Boolean).forEach((c) => svg.classList.add(c))
    if (el.parentNode) el.parentNode.replaceChild(svg, el)
  })
}
